import {
  type ActivityDto,
  type AttachmentDto,
  ago,
  type ConfigLists,
  findItem,
  fmtDate,
  fmtDateTime,
  type PeopleMap,
  pausedNote,
  type TicketDto,
} from '@hrms/shared'
import type { ReactNode } from 'react'
import { hint } from '../../../lib/hint.ts'
import { PersonAvatar, personName, StatusLozenge, UNKNOWN_PRIORITY_NAME } from './ui.tsx'

interface SentenceCtx {
  people: PeopleMap
  config: ConfigLists
  ticket: TicketDto
  now: number
  attachments?: AttachmentDto[]
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-semibold text-text">{children}</strong>
)

function nameOf(
  list: readonly { id: string; name: string; archived: boolean }[],
  id: string | null,
) {
  return findItem(list, id)?.name
}

/** The sentence for one activity entry (PRD "Activity tab", DESIGN.md §3.5). */
export function activitySentence(a: ActivityDto, ctx: SentenceCtx): ReactNode {
  const who = <B>{personName(ctx.people, a.actorId)}</B>
  const chip = (id: string | null) =>
    id ? (
      <StatusLozenge statusId={id} statuses={ctx.config.statuses} className="align-middle" />
    ) : null
  switch (a.type) {
    case 'created':
      return a.actorId !== ctx.ticket.raisedBy ? (
        <>
          {who} logged this ticket for <B>{personName(ctx.people, ctx.ticket.raisedBy)}</B>
        </>
      ) : (
        <>{who} raised this ticket</>
      )
    case 'status': {
      const note = pausedNote(a.meta)
      return (
        <>
          {who} {a.meta?.auto ? 'replied, so the ticket moved from' : 'changed status from'}{' '}
          {chip(a.fromValue)} to {chip(a.toValue)}
          {note && `. ${note}`}
        </>
      )
    }
    case 'assignee':
      return a.toValue ? (
        <>
          {who} assigned this to <B>{personName(ctx.people, a.toValue)}</B>
        </>
      ) : (
        <>{who} removed the assignee</>
      )
    case 'priority':
      return (
        <>
          {who} changed priority from{' '}
          <B>{nameOf(ctx.config.priorities, a.fromValue) ?? UNKNOWN_PRIORITY_NAME}</B> to{' '}
          <B>{nameOf(ctx.config.priorities, a.toValue) ?? UNKNOWN_PRIORITY_NAME}</B>
        </>
      )
    case 'category':
      return (
        <>
          {who} moved this from{' '}
          <B>{nameOf(ctx.config.categories, a.fromValue) ?? 'Uncategorised'}</B> to{' '}
          <B>{nameOf(ctx.config.categories, a.toValue) ?? 'Uncategorised'}</B>
        </>
      )
    case 'eta':
      return a.meta?.reset || !a.toValue ? (
        <>{who} reset the ETA to the SLA</>
      ) : (
        <>
          {who} set the ETA to <B>{fmtDate(Number(a.toValue), { now: ctx.now })}</B>
        </>
      )
    case 'confidential':
      return a.toValue === 'true' ? (
        <>{who} marked this confidential</>
      ) : (
        <>{who} removed confidential</>
      )
    case 'reopened':
      return <>{who} reopened this ticket</>
    case 'attachment': {
      const ids = (a.toValue ?? '').split(',').filter(Boolean).map(Number)
      const names = ids.flatMap((id) => ctx.attachments?.find((f) => f.id === id)?.name ?? [])
      if (names.length === ids.length && names.length > 0 && names.length <= 3)
        return (
          <>
            {who} attached <B>{listNames(names)}</B>
          </>
        )
      return (
        <>
          {who} attached {ids.length === 1 ? 'a file' : `${ids.length || 'some'} files`}
        </>
      )
    }
  }
}

export function ActivityList({ activity, ...ctx }: SentenceCtx & { activity: ActivityDto[] }) {
  if (activity.length === 0) return <p className="py-6 text-center text-muted">No activity yet.</p>
  return (
    <ol className="flex flex-col gap-3.5">
      {activity.map((a) => (
        <li
          key={a.id}
          className="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-muted"
        >
          <PersonAvatar person={ctx.people[a.actorId]} id={a.actorId} className="mt-0.5" />
          <p className="min-w-0 flex-1">
            {activitySentence(a, ctx)}
            <span className="whitespace-nowrap text-faint">
              {' · '}
              <time
                dateTime={new Date(a.createdAt).toISOString()}
                {...hint(fmtDateTime(a.createdAt, { now: ctx.now }))}
              >
                {ago(a.createdAt, { now: ctx.now })}
              </time>
            </span>
          </p>
        </li>
      ))}
    </ol>
  )
}
